import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, finalize, takeUntil } from 'rxjs';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { CxSelectComponent } from 'src/app/shared/components/cx-select/cx-select.component';
import { OutboundNumberService } from '../../services/outbound-number.service';
import { OutboundNumberDto } from '../../models/outbound-number.dto';
import { SipTransportProtocol } from '../../models/sip-transport-protocol.enum';
import { PROTOCOL_OPTIONS } from '../../models/protocol-options.constant';
import { REGISTER_OPTIONS } from '../../models/register-options.constant';

@Component({
  selector: 'app-outbound-number-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    LocalizationPipe,
    DialogModule,
    InputTextModule,
    PasswordModule,
    CxSelectComponent,
    ToggleSwitchModule,
  ],
  templateUrl: './outbound-number-form.component.html',
  styleUrl: './outbound-number-form.component.scss',
})
export class OutboundNumberFormComponent implements OnInit, OnChanges, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly outboundNumberService = inject(OutboundNumberService);
  private readonly localization = inject(LocalizationService);
  private readonly destroy$ = new Subject<void>();

  @Input() visible = false;
  @Input() isEdit = false;
  @Input() model: OutboundNumberDto | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<boolean>();

  dialogVisible = false;
  submitting = false;

  readonly protocolOptions = PROTOCOL_OPTIONS;
  registerOptions: { value: boolean; label: string }[] = [];

  form: FormGroup = this.fb.group({
    phoneNumber: [
      '',
      [Validators.required, Validators.maxLength(32), Validators.pattern(/^[0-9+]*$/)],
    ],
    gatewayUsername: [''],
    gatewayPassword: [''],
    gatewayRealm: [''],
    gatewayRegister: [true],
    protocol: [SipTransportProtocol.UDP],
    isDefault: [false],
  });

  ngOnInit(): void {
    this.dialogVisible = !!this.visible;
    this.buildLocalizedOptions();
    this.localization.languageChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.buildLocalizedOptions());
    this.applyModelToForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildLocalizedOptions(): void {
    this.registerOptions = REGISTER_OPTIONS.map(o => ({
      value: o.value,
      label: this.localization.instant(o.label),
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']) {
      this.dialogVisible = !!this.visible;
    }
    if (changes['model'] || changes['isEdit']) {
      this.applyModelToForm();
    }
  }

  get titleKey(): string {
    return this.isEdit
      ? 'Settings::PhoneNumbers:EditOutboundNumber'
      : 'Settings::PhoneNumbers:AddOutboundNumber';
  }

  get submitLabelKey(): string {
    return this.isEdit ? 'Settings::PhoneNumbers:Update' : 'Settings::PhoneNumbers:Save';
  }

  get registerEnabled(): boolean {
    return this.form.get('gatewayRegister')?.value === true;
  }

  private applyModelToForm(): void {
    if (!this.form) {
      return;
    }
    if (this.isEdit && this.model) {
      this.form.patchValue({
        phoneNumber: this.model.phoneNumber,
        gatewayUsername: this.model.gatewayUsername ?? '',
        gatewayPassword: this.model.gatewayPassword ?? '',
        gatewayRealm: this.model.gatewayRealm ?? '',
        gatewayRegister: this.model.gatewayRegister ?? true,
        protocol: this.model.protocol ?? SipTransportProtocol.UDP,
        isDefault: this.model.isDefault ?? false,
      });
    } else {
      this.form.reset({
        phoneNumber: '',
        gatewayUsername: '',
        gatewayPassword: '',
        gatewayRealm: '',
        gatewayRegister: true,
        protocol: SipTransportProtocol.UDP,
        isDefault: false,
      });
    }
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  cancel(): void {
    this.visibleChange.emit(false);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const register = value.gatewayRegister as boolean;
    const dto: OutboundNumberDto = {
      phoneNumber: (value.phoneNumber as string).trim(),
      gatewayUsername: (value.gatewayUsername as string)?.trim() || undefined,
      gatewayPassword: (value.gatewayPassword as string) || undefined,
      gatewayRealm: (value.gatewayRealm as string)?.trim() || undefined,
      gatewayRegister: register,
      protocol: register ? (value.protocol as SipTransportProtocol) : undefined,
      isDefault: value.isDefault as boolean,
    } as OutboundNumberDto;

    this.submitting = true;

    const editId = this.isEdit ? this.model?.id : undefined;
    const isCreate = editId == null;
    const request$ =
      editId == null
        ? this.outboundNumberService.create(dto)
        : this.outboundNumberService.update({ ...dto, id: editId });

    request$.pipe(finalize(() => (this.submitting = false))).subscribe({
      next: () => this.onSuccess(isCreate),
      error: () => {},
    });
  }

  private onSuccess(isCreate: boolean): void {
    this.submitting = false;
    this.saved.emit(isCreate);
    this.visibleChange.emit(false);
  }
}
